/**
 * HexVariant - Variante Hexadecimal para Color
 *
 * Representa cor como string hexadecimal.
 *
 * @example
 * "#ff0000"     // RGB (6 chars)
 * "#ff0000ff"   // RGBA (8 chars)
 * "#f00"        // RGB shorthand (3 chars)
 * "#f00f"       // RGBA shorthand (4 chars)
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { RGBA, ColorHex, ColorFormatOptions } from "../types";
import {
  NAMED_COLORS,
  normalizeRgbChannel,
  normalizeAlpha,
  hslToRgb,
} from "../types";

// Regex patterns
const HEX_REGEX_3 = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_REGEX_4 = /^#?([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_REGEX_6 = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX_REGEX_8 = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

export class HexVariant extends BaseVariant<ColorHex, RGBA> {
  readonly name = "hex";
  readonly type = "string" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: ColorHex): RGBA {
    const hex = value.trim();

    // Try 8-char hex (#rrggbbaa)
    let match = hex.match(HEX_REGEX_8);
    if (match) {
      return {
        r: parseInt(match[1], 16),
        g: parseInt(match[2], 16),
        b: parseInt(match[3], 16),
        a: parseInt(match[4], 16) / 255,
      };
    }

    // Try 6-char hex (#rrggbb)
    match = hex.match(HEX_REGEX_6);
    if (match) {
      return {
        r: parseInt(match[1], 16),
        g: parseInt(match[2], 16),
        b: parseInt(match[3], 16),
        a: 1,
      };
    }

    // Try 4-char hex (#rgba)
    match = hex.match(HEX_REGEX_4);
    if (match) {
      return {
        r: parseInt(match[1] + match[1], 16),
        g: parseInt(match[2] + match[2], 16),
        b: parseInt(match[3] + match[3], 16),
        a: parseInt(match[4] + match[4], 16) / 255,
      };
    }

    // Try 3-char hex (#rgb)
    match = hex.match(HEX_REGEX_3);
    if (match) {
      return {
        r: parseInt(match[1] + match[1], 16),
        g: parseInt(match[2] + match[2], 16),
        b: parseInt(match[3] + match[3], 16),
        a: 1,
      };
    }

    throw new Error(`Invalid hex color: ${value}`);
  }

  fromBase(base: RGBA): ColorHex {
    const r = normalizeRgbChannel(base.r).toString(16).padStart(2, "0");
    const g = normalizeRgbChannel(base.g).toString(16).padStart(2, "0");
    const b = normalizeRgbChannel(base.b).toString(16).padStart(2, "0");

    const alpha = normalizeAlpha(base.a);

    // Include alpha only if not fully opaque
    if (alpha < 1) {
      const a = Math.round(alpha * 255)
        .toString(16)
        .padStart(2, "0");
      return `#${r}${g}${b}${a}`;
    }

    return `#${r}${g}${b}`;
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): ColorHex | null {
    // Already a string
    if (typeof input === "string") {
      const trimmed = input.trim().toLowerCase();

      // Named color
      if (NAMED_COLORS[trimmed]) {
        return this.fromBase(NAMED_COLORS[trimmed]);
      }

      // Hex format (with or without #)
      if (
        HEX_REGEX_8.test(trimmed) ||
        HEX_REGEX_6.test(trimmed) ||
        HEX_REGEX_4.test(trimmed) ||
        HEX_REGEX_3.test(trimmed)
      ) {
        // Normalize to #rrggbb or #rrggbbaa
        try {
          const rgba = this.toBase(trimmed);
          return this.fromBase(rgba);
        } catch {
          return null;
        }
      }

      // Try rgb()/rgba() string
      const rgbMatch = trimmed.match(
        /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (rgbMatch) {
        const rgba: RGBA = {
          r: parseInt(rgbMatch[1], 10),
          g: parseInt(rgbMatch[2], 10),
          b: parseInt(rgbMatch[3], 10),
          a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
        };
        return this.fromBase(rgba);
      }

      // Try hsl()/hsla() string
      const hslMatch = trimmed.match(
        /^hsla?\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (hslMatch) {
        const rgb = hslToRgb(
          parseInt(hslMatch[1], 10),
          parseInt(hslMatch[2], 10),
          parseInt(hslMatch[3], 10),
        );
        const rgba: RGBA = {
          ...rgb,
          a: hslMatch[4] ? parseFloat(hslMatch[4]) : 1,
        };
        return this.fromBase(rgba);
      }

      return null;
    }

    // Object with r, g, b properties
    if (
      typeof input === "object" &&
      input !== null &&
      "r" in input &&
      "g" in input &&
      "b" in input
    ) {
      const obj = input as { r: number; g: number; b: number; a?: number };
      const rgba: RGBA = {
        r: obj.r,
        g: obj.g,
        b: obj.b,
        a: obj.a ?? 1,
      };
      return this.fromBase(rgba);
    }

    // Object with h, s, l properties
    if (
      typeof input === "object" &&
      input !== null &&
      "h" in input &&
      "s" in input &&
      "l" in input
    ) {
      const obj = input as { h: number; s: number; l: number; a?: number };
      const rgb = hslToRgb(obj.h, obj.s, obj.l);
      const rgba: RGBA = {
        ...rgb,
        a: obj.a ?? 1,
      };
      return this.fromBase(rgba);
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: ColorHex): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "string") {
      errors.push("Hex color must be a string");
      return { valid: false, errors };
    }

    const hex = value.trim();

    if (
      !HEX_REGEX_8.test(hex) &&
      !HEX_REGEX_6.test(hex) &&
      !HEX_REGEX_4.test(hex) &&
      !HEX_REGEX_3.test(hex)
    ) {
      errors.push("Invalid hex format. Use #rgb, #rgba, #rrggbb, or #rrggbbaa");
      return { valid: false, errors };
    }

    return { valid: true, errors: [] };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: ColorHex, options?: FormatOptions): string {
    const opts = options as ColorFormatOptions | undefined;

    try {
      const rgba = this.toBase(value);
      let hex = this.fromBase(rgba);

      // Uppercase
      if (opts?.uppercase) {
        hex = hex.toUpperCase();
      }

      // Include alpha even if 1
      if (opts?.includeAlpha && rgba.a === 1) {
        const a = "ff";
        hex = hex.length === 7 ? `${hex}${opts.uppercase ? "FF" : "ff"}` : hex;
      }

      // Compact (shorthand if possible)
      if (opts?.compact && hex.length === 7) {
        const canShorten =
          hex[1] === hex[2] && hex[3] === hex[4] && hex[5] === hex[6];
        if (canShorten) {
          hex = `#${hex[1]}${hex[3]}${hex[5]}`;
          if (opts.uppercase) hex = hex.toUpperCase();
        }
      }

      return hex;
    } catch {
      return value;
    }
  }
}
